/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { A3D_TAG_NAMES } from '@a3d-viewer/viewer/core/a3d-element';
/**
 * The loading screen component for the viewer.
 * @element a3d-loading-screen
 * @property {string} default-text - The default text to display when no text is provided
 * @cssprop --loading-spinner-color - The color of the loading spinner
 */
let A3dLoadingScreen = class A3dLoadingScreen extends LitElement {
    constructor() {
        super();
        /**
         * The default text to display when no text is provided.
         * @type {string}
         */
        this.defaultText = 'Loading...';
        /**
         * The loading progress.
         * @type {boolean | number} - The loading progress value return by the viewer.
         */
        this._loadingProgress = false;
        this._firstLoad = true;
        this._abortController = new AbortController();
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.getAttribute('slot') !== 'loading-progress') {
            this.setAttribute('slot', 'loading-progress');
        }
        if (!(this.parentElement?.tagName === A3D_TAG_NAMES.VIEWER)) {
            throw new Error('a3d-loading-screen must be a child of a3d-viewer');
        }
        this.parentElement.addEventListener('loadingprogresschange', this._onLoadingProgressChange.bind(this), { signal: this._abortController.signal });
        this.parentElement.addEventListener('sceneloaded', this._onSceneLoaded.bind(this), { signal: this._abortController.signal });
        this.parentElement.addEventListener('modelerror', this._onError.bind(this), {
            signal: this._abortController.signal,
        });
        this.parentElement.addEventListener('environmenterror', this._onError.bind(this), { signal: this._abortController.signal });
        // if the loading screen is defined after the viewer is loaded,
        // we need to check if the scene is already loaded
        if (this.parentElement.loaded) {
            this._onSceneLoaded();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        // Remove event listeners
        this._abortController.abort();
    }
    /**
     * Called when the loading progress changes.
     * @param e The event.
     */
    _onLoadingProgressChange(e) {
        const viewer = e.target;
        this._loadingProgress = viewer.loadingProgress;
    }
    /**
     * Called when the scene is loaded.
     */
    _onSceneLoaded() {
        this._firstLoad = false;
    }
    /**
     * Called when an error occurs in the viewer.
     */
    _onError() {
        this._loadingProgress = false;
        this._firstLoad = false;
    }
    render() {
        if (this._loadingProgress === false && this._firstLoad === false) {
            return html ``;
        }
        else if (this.hasChildNodes()) {
            return html `<slot></slot>`;
        }
        else {
            return html `<div class="a3d-loading-screen">
        <div class="a3d-loading-screen__spinner-container">
          <svg
            class="a3d-loading-screen__spinner-svg"
            stroke="white"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <style></style>
            <g>
              <circle cx="12" cy="12" r="9.5" stroke-width="3"></circle>
            </g>
          </svg>
        </div>
        <div class="a3d-loading-screen__text">
          <slot name="loading-text">${this.defaultText}</slot>
        </div>
      </div>`;
        }
    }
};
A3dLoadingScreen.styles = css `
    :host {
      pointer-events: none;
      position: absolute;
      inset: 0;
    }

    .a3d-loading-screen {
      display: flex;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #ffffffaa;
    }

    .a3d-loading-screen__spinner-svg {
      transform-origin: center;
      animation: spinner_rotation 2s linear infinite;
      width: 50px;
      height: 50px;
    }

    .a3d-loading-screen__spinner-svg circle {
      stroke: var(
        --loading-spinner-color,
        var(--spectrum-alias-text-color-key-focus, #0054b6)
      );
      stroke-linecap: round;
      animation: spinner_length_variations 1.5s ease-in-out infinite;
      fill: none;
    }

    @keyframes spinner_rotation {
      100% {
        transform: rotate(360deg);
      }
    }

    @keyframes spinner_length_variations {
      0% {
        stroke-dasharray: 0 150;
        stroke-dashoffset: 0;
      }
      47.5% {
        stroke-dasharray: 42 150;
        stroke-dashoffset: -16;
      }
      95%,
      100% {
        stroke-dasharray: 42 150;
        stroke-dashoffset: -59;
      }
    }
  `;
__decorate([
    property({
        attribute: 'default-text',
    })
], A3dLoadingScreen.prototype, "defaultText", void 0);
__decorate([
    state()
], A3dLoadingScreen.prototype, "_loadingProgress", void 0);
__decorate([
    state()
], A3dLoadingScreen.prototype, "_firstLoad", void 0);
A3dLoadingScreen = __decorate([
    customElement('a3d-loading-screen')
], A3dLoadingScreen);
export { A3dLoadingScreen };
